import { PATH_WIDTH, STATION_DESCRIPTION_WIDTH, STATION_SIZE, Y_STEP_UP } from 'constants/map';
import { Point } from '../../../../Path/types';
import { SubZoneStation } from '..';
import { Station } from 'pages/Map/types';
import { SUBZONE_LEFT_SPACE } from '../SubZoneStation';
import { Path } from '../../../../Path';

const farLeftPosition = {
    x: 0,
    y: 0
};
const farRightPosition = {
    x: Number.MAX_SAFE_INTEGER,
    y: Number.MAX_SAFE_INTEGER
};

const findBoundariesOfPath = (points: Point[]) => {
    return points.reduce(
        ({ topLeft, bottomRight }, point) => {
            return {
                topLeft: {
                    x: Math.min(topLeft.x, point.x - PATH_WIDTH),
                    y: Math.min(topLeft.y, point.y)
                },
                bottomRight: {
                    x: Math.max(bottomRight.x, point.x + PATH_WIDTH),
                    y: Math.max(bottomRight.y, point.y)
                }
            };
        },
        {
            topLeft: farRightPosition,
            bottomRight: farLeftPosition
        }
    );
};

/**
 * Finds the most top left and bottom right positions in all paths to identify the area occupied by the paths
 */
const findBoundariesOfPaths = (paths: Path[]) => {
    return paths.reduce(
        ({ topLeft, bottomRight }, path) => {
            const { topLeft: pathTopLeft, bottomRight: pathBottomRight } = findBoundariesOfPath(path.getPoints());

            return {
                topLeft: {
                    x: Math.min(topLeft.x, pathTopLeft.x),
                    y: Math.min(topLeft.y, pathTopLeft.y)
                },
                bottomRight: {
                    x: Math.max(bottomRight.x, pathBottomRight.x),
                    y: Math.max(bottomRight.y, pathBottomRight.y)
                }
            };
        },
        {
            topLeft: farRightPosition,
            bottomRight: farLeftPosition
        }
    );
};

/**
 * Finds the top left and bottom right positions of subZone based on
 * - Positions occupied by its paths
 * - Positions needed to accommadate its stations along with description
 */
const findSubZoneBoundaries = (subZone: SubZoneStation, stations: Station[], paths: Path[]) => {
    const subZoneStationIds = stations.filter(({ parentStation }) => parentStation?.id === subZone.id).map(({ id }) => id);
    const subZonePaths = paths.filter(({ source, target, isLoopBack }) => {
        const isSubZonePath = subZoneStationIds.includes(source.id) || subZoneStationIds.includes(target.id);
        if (isLoopBack) {
            // Ignores loopback connects outside station
            return isSubZonePath && source.parentStation?.id === target.parentStation?.id;
        }
        return isSubZonePath;
    });
    const { topLeft: pathsTopLeft, bottomRight: pathsBottomRight } = findBoundariesOfPaths(subZonePaths);

    const leftChildAbsolutePosition = subZone.getLeftMostChild().getAbsolutePosition();
    const rightChildAbsolutePosition = subZone.getRightMostChild().getAbsolutePosition();
    const bottomChildAbsolutePosition = subZone.getBottomMostChild().getAbsolutePosition();

    return {
        topLeft: {
            x: Math.min(pathsTopLeft.x, leftChildAbsolutePosition.x - SUBZONE_LEFT_SPACE),
            y: pathsTopLeft.y
        },
        bottomRight: {
            x: Math.max(pathsBottomRight.x, rightChildAbsolutePosition.x + STATION_DESCRIPTION_WIDTH),
            y: Math.max(pathsBottomRight.y, bottomChildAbsolutePosition.y + 200)
        }
    };
};

/**
 * Finding all nearest outside stations (on left, right, top, bottom) of subzone to restrict subzone area going beyond them
 * So finds the max area that subZone can grow based on stations and add gaps which avoid collision with them
 * Ex: Subzone may have path connecting to outside station
 * - in this case Path will give positions till the outside station but we shouldn't grow subzone till outside station
 */
const findSubZoneMaxBoundaries = (subZone: SubZoneStation, stations: Station[]) => {
    const { x: subZoneX, y: subZoneY } = subZone.getAbsolutePosition();
    const outSideStations = stations.filter(station => {
        if (station.id === subZone.id || station.isHidden(subZone)) {
            return false;
        }
        return station.parentStation ? station.parentStation.id != subZone.id : true;
    });
    return outSideStations.reduce(
        ({ topLeft, bottomRight }, station) => {
            const { x, y } = station.getAbsolutePosition();

            const minX = x < subZoneX ? x + STATION_DESCRIPTION_WIDTH + STATION_SIZE : topLeft.x; //to avoid collision with Station Description
            const maxX = x > subZoneX ? x - 100 : bottomRight.x; //To avoid collision with timeline
            const minY = y < subZoneY ? y + Y_STEP_UP / 2 : topLeft.y; // To have some gap below top station
            const maxY = y > subZoneY ? y - Y_STEP_UP / 2 + PATH_WIDTH : bottomRight.y; // To have some gap above bottom station
            return {
                topLeft: { x: Math.max(topLeft.x, minX), y: Math.max(topLeft.y, minY) },
                bottomRight: { x: Math.min(bottomRight.x, maxX), y: Math.min(bottomRight.y, maxY) }
            };
        },
        {
            topLeft: farLeftPosition,
            bottomRight: farRightPosition
        }
    );
};

/**
 * Calculate subzone boundaries based on subzone paths & station positions and subzone max boundaries based on outside stations
 * Finding topLeft and bottomRight positions is enough, we can derive bottomLeft/topRight if needed from these
 */
export const calculateSubZoneBoundaries = (subZone: SubZoneStation, stations: Station[], paths: Path[]) => {
    const { topLeft, bottomRight } = findSubZoneBoundaries(subZone, stations, paths);
    const { topLeft: maxTopLeft, bottomRight: maxBottomRIght } = findSubZoneMaxBoundaries(subZone, stations);

    return {
        topLeft: { x: Math.max(topLeft.x, maxTopLeft.x), y: Math.max(topLeft.y, maxTopLeft.y) },
        bottomRight: { x: Math.min(bottomRight.x, maxBottomRIght.x), y: Math.min(bottomRight.y, maxBottomRIght.y) }
    };
};
